import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Siren, Phone, Car, UserSquare2, MapPin, FileText, CheckCircle2,
  ArrowRight, Zap, Database, Microscope,
} from "lucide-react";

/**
 * Police Intelligence Capture — officer pastes an identifier and gets an
 * immediate "What MUKTI already knows" response.
 */
export default function PoliceCapture() {
  const [form, setForm] = useState({
    phone: "", vehicle: "", alias: "", locality: "", note: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const hasInput = form.phone || form.vehicle || form.alias || form.locality;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!hasInput) {
      toast.error("Enter at least one identifier.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/police-intel/capture", form);
      setResult(data);
      toast.success("Captured — see what MUKTI knows below.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Capture failed.");
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setResult(null);
    setForm({ phone: "", vehicle: "", alias: "", locality: "", note: "" });
  };

  return (
    <div className="space-y-8 pb-10" data-testid="police-capture-root">
      <div>
        <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-slate-500">
          <Siren className="h-4 w-4 text-red-600" />
          <span>Police intelligence capture</span>
        </div>
        <h1 className="font-display font-bold text-3xl sm:text-4xl mt-2">
          Paste what you have.
          <span className="text-orange-600"> MUKTI tells you what it knows.</span>
        </h1>
        <p className="mt-3 text-slate-600 max-w-2xl text-sm sm:text-base leading-relaxed">
          Enter a phone, vehicle, alias, or locality from a rescue, informant, or arrest.
          MUKTI checks it against every ingested advertisement and existing lead and returns
          the match in seconds.
        </p>
      </div>

      <form
        onSubmit={onSubmit}
        className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 sm:p-7 space-y-5"
        data-testid="police-capture-form"
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <Field icon={Phone} label="Phone number" value={form.phone} onChange={update("phone")}
                 placeholder="e.g. 98450 11220" testid="capture-phone" inputMode="tel" />
          <Field icon={Car} label="Vehicle number" value={form.vehicle} onChange={update("vehicle")}
                 placeholder="e.g. KA 05 BL 1234" testid="capture-vehicle" />
          <Field icon={UserSquare2} label="Name / alias" value={form.alias} onChange={update("alias")}
                 placeholder="e.g. Raju, Moni" testid="capture-alias" />
          <Field icon={MapPin} label="Locality / area" value={form.locality} onChange={update("locality")}
                 placeholder="e.g. Majestic, Yeshwanthpur" testid="capture-locality" />
        </div>
        <div>
          <label className="text-xs font-mono uppercase tracking-wider text-slate-500 flex items-center gap-1">
            <FileText className="h-3.5 w-3.5" /> Context (optional)
          </label>
          <Textarea
            value={form.note}
            onChange={update("note")}
            placeholder="Where was this seen? Rescue, informant tip, patrol stop?"
            rows={3}
            className="mt-1"
            data-testid="capture-note"
          />
        </div>
        <div className="flex items-center justify-between gap-3">
          <div className="text-xs text-slate-500 font-mono tracking-wider uppercase">
            {hasInput ? "Ready to query" : "Enter at least one identifier"}
          </div>
          <div className="flex gap-2">
            {result && (
              <Button type="button" variant="outline" onClick={reset}
                      data-testid="capture-reset-btn">
                New capture
              </Button>
            )}
            <Button
              type="submit" disabled={!hasInput || submitting}
              data-testid="capture-submit-btn"
              className="bg-slate-900 hover:bg-slate-800"
            >
              <Zap className="h-4 w-4 mr-1" />
              {submitting ? "Checking…" : "Check what MUKTI knows"}
            </Button>
          </div>
        </div>
      </form>

      {result && <KnowsCard result={result} />}
    </div>
  );
}

function Field({ icon: Icon, label, value, onChange, placeholder, testid, inputMode }) {
  return (
    <div>
      <label className="text-xs font-mono uppercase tracking-wider text-slate-500 flex items-center gap-1">
        <Icon className="h-3.5 w-3.5" /> {label}
      </label>
      <Input
        value={value} onChange={onChange} placeholder={placeholder}
        className="mt-1 h-11" data-testid={testid} inputMode={inputMode}
      />
    </div>
  );
}

function Stat({ value, label }) {
  return (
    <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
      <div className="text-2xl font-display font-bold text-slate-900">{value}</div>
      <div className="text-[11px] font-mono uppercase tracking-wider text-slate-500 mt-0.5">{label}</div>
    </div>
  );
}

function KnowsCard({ result }) {
  const k = result.mukti_knows || {};
  const stands = result.stands_out || [];
  const leads = result.existing_leads || [];
  const ads = result.ads_preview || [];
  const nothing = (k.ads || 0) === 0 && leads.length === 0;

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden"
         data-testid="capture-result">
      <div className="bg-slate-900 text-white px-5 py-4 flex items-center gap-3">
        <CheckCircle2 className="h-5 w-5 text-green-400" />
        <div className="flex-1">
          <div className="text-xs font-mono uppercase tracking-wider opacity-70">Captured as OFFICER FINDING</div>
          <div className="font-semibold">MUKTI knows about <span className="font-mono text-orange-300">{result.probe}</span></div>
        </div>
      </div>

      <div className="p-5 sm:p-7 space-y-6">
        {nothing ? (
          <div className="text-sm text-slate-600 bg-amber-50 border border-amber-200 rounded-lg p-4">
            <strong>Nothing on file yet.</strong> This capture has been saved as an officer finding.
            If this identifier appears again in future ads or reports, it will be surfaced on the Lead Room.
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              <Stat value={k.ads} label="Ads" />
              <Stat value={k.aliases} label="Aliases" />
              <Stat value={k.phones} label="Phones" />
              <Stat value={k.images} label="Photos" />
              <Stat value={k.publications} label="Publications" />
              <Stat value={k.locations} label="Localities" />
            </div>

            {stands.length > 0 && (
              <div>
                <div className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-2">What stands out</div>
                <ul className="space-y-2 text-sm">
                  {stands.map((s, i) => (
                    <li key={i} className="flex gap-2 text-slate-700">
                      <span className="h-1.5 w-1.5 rounded-full bg-orange-500 mt-2 shrink-0" />
                      <span>{s}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {leads.length > 0 && (
              <div>
                <div className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1">
                  <Microscope className="h-3.5 w-3.5" /> Appears in existing leads
                </div>
                <ul className="space-y-2">
                  {leads.slice(0, 5).map((L) => (
                    <li key={L.id}>
                      <Link
                        to={`/leads/${L.id}`}
                        className="flex items-center justify-between p-3 rounded-lg border border-slate-200 hover:border-slate-900 hover:bg-slate-50 transition-colors"
                        data-testid={`capture-lead-link-${L.id}`}
                      >
                        <div>
                          <div className="font-semibold text-slate-900 text-sm">{L.title}</div>
                          <div className="text-[11px] font-mono uppercase tracking-wider text-slate-500 mt-0.5">
                            <Badge variant="outline" className="mr-1">{L.priority?.replace("_", " ")}</Badge>
                            {L.status}
                          </div>
                        </div>
                        <ArrowRight className="h-4 w-4 text-slate-400" />
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {ads.length > 0 && (
              <div>
                <div className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1">
                  <Database className="h-3.5 w-3.5" /> Supporting advertisements
                </div>
                <ul className="space-y-1 text-sm">
                  {ads.slice(0, 6).map((a) => (
                    <li key={a.id}>
                      <Link to={`/ads/${a.id}`} className="hover:underline text-slate-700 line-clamp-1">
                        {a.title} <span className="text-slate-400">· {a.source || "—"} · {a.locality || "—"}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
